import React, { useState, useMemo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import callPiggybackAPI from "../Helpers/piggyback.api.helper";
import {
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Chip,
  InputAdornment,
  Card,
  CardContent,
  Avatar,
  Divider,
  MenuItem,
  CircularProgress,
  IconButton,
  Tooltip,
  Pagination
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { toast } from "react-toastify";
import { setTruInkSelectedSiteOption } from "../../../redux/slices/truInk";
import { useTheme, useMediaQuery } from "@mui/material";

const CATEGORIES = ["All", "Hospitality", "Meeting Management", "Retail"];
const NAVIGATABLE_OPTIONS = ["TruInk EMMS"];
const SMART_SETTINGS_PLUGINS = ["Smart Icons"];

const DOW_OPTIONS = [
  { value: "0", label: "Sunday" },
  { value: "1", label: "Monday" },
  { value: "2", label: "Tuesday" },
  { value: "3", label: "Wednesday" },
  { value: "4", label: "Thursday" },
  { value: "5", label: "Friday" },
  { value: "6", label: "Saturday" },
];

const HOURS = Array.from({ length: 24 }, (_, i) => String(i));
const MINUTES = Array.from({ length: 60 }, (_, i) => String(i));

function parseCronToUI(cron) {
  if (!cron) return { _freq: "daily", _hour: "9", _minute: "0", _dow: "1", _dom: "1" };
  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return { _freq: "custom", _hour: "9", _minute: "0", _dow: "1", _dom: "1" };
  const [min, hour, dom, month, dow] = parts;
  if (dom === "*" && month === "*" && dow === "*" && /^\d+$/.test(hour) && /^\d+$/.test(min))
    return { _freq: "daily", _hour: hour, _minute: min, _dow: "1", _dom: "1" };
  if (dom === "*" && month === "*" && dow !== "*" && /^\d+$/.test(dow) && /^\d+$/.test(hour) && /^\d+$/.test(min))
    return { _freq: "weekly", _hour: hour, _minute: min, _dow: dow, _dom: "1" };
  if (dom !== "*" && month === "*" && dow === "*" && /^\d+$/.test(dom) && /^\d+$/.test(hour) && /^\d+$/.test(min))
    return { _freq: "monthly", _hour: hour, _minute: min, _dow: "1", _dom: dom };
  return { _freq: "custom", _hour: "9", _minute: "0", _dow: "1", _dom: "1" };
}

function buildCron(freq, hour, minute, dow, dom) {
  const h = hour || "9";
  const m = minute || "0";
  if (freq === "daily") return `${m} ${h} * * *`;
  if (freq === "weekly") return `${m} ${h} * * ${dow || "1"}`;
  if (freq === "monthly") return `${m} ${h} ${dom || "1"} * *`;
  return "";
}

const EMPTY_SCHEDULE = {
  cronExpression: "0 9 * * *",
  collectionId: "",
  templateId: "",
  _freq: "daily",
  _hour: "9",
  _minute: "0",
  _dow: "1",
  _dom: "1",
};

function PluginsPanel() {
  const dispatch = useDispatch();
  const selectedSite = useSelector((state) => state.truInk.selectedSite);

  const [plugins, setPlugins] = useState([]);
  const [myPluginIds, setMyPluginIds] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [searchText, setSearchText] = useState("");

  const [configOpen, setConfigOpen] = useState(false);
  const [selectedPlugin, setSelectedPlugin] = useState(null);
  const [schedules, setSchedules] = useState([{ ...EMPTY_SCHEDULE }]);
  const [collections, setCollections] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [configLoading, setConfigLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmDeleteIndex, setConfirmDeleteIndex] = useState(null);

  const [smartSettingsOpen, setSmartSettingsOpen] = useState(false);
  const [smartPlugin, setSmartPlugin] = useState(null);
  const [smartColumn, setSmartColumn] = useState("");
  const [smartSpacing, setSmartSpacing] = useState("");
  const [availableFields, setAvailableFields] = useState([]);
  const [smartLoading, setSmartLoading] = useState(false);
  const [smartSaving, setSmartSaving] = useState(false);
  const [labels, setLabels] = useState([]);
  const [newLabelName, setNewLabelName] = useState("");
  const [newLabelImageUrl, setNewLabelImageUrl] = useState("");
  const [addingLabel, setAddingLabel] = useState(false);
 const [currentPage, setCurrentPage] = useState(1);
const theme = useTheme();
const isMobile = useMediaQuery(theme.breakpoints.down("md"));
const isTallMobile = useMediaQuery("(min-height:770px)");

const PLUGINS_PER_PAGE = useMemo(() => {
  if (isMobile) return isTallMobile ? 2 : 1;
  return 3;
}, [isMobile, isTallMobile]);




  useEffect(() => {
    const fetchPlugins = async () => {
      setLoading(true);
      try {
        const response = await callPiggybackAPI("plugins", {}, {}, "GET");
        if (Array.isArray(response)) setPlugins(response);
        else setPlugins([]);
      } catch (err) {
        console.error("Failed to fetch plugins:", err);
        setPlugins([]);
      } finally {
        setLoading(false);
      }
    };
    fetchPlugins();
  }, []);

  useEffect(() => {
    if (!selectedSite) return;
    callPiggybackAPI(
      "pluginSettings",
      { siteId: selectedSite.id, tenantId: selectedSite.tenantId },
      {},
      "GET"
    )
      .then((res) => {
        const ids = Array.isArray(res) ? res.map((s) => s.pluginId) : [];
        setMyPluginIds(ids);
      })
      .catch(() => setMyPluginIds([]));
  }, [selectedSite]);

  const filteredPlugins = useMemo(() => {
    if (myPluginIds === null) return [];
    return plugins.filter((p) => {
      if (!myPluginIds.includes(p.id)) return false;
      const matchesCategory = selectedCategory === "All" || p.category === selectedCategory;
      const matchesSearch =
        !searchText ||
        p.title.toLowerCase().includes(searchText.toLowerCase()) ||
        p.description.toLowerCase().includes(searchText.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [selectedCategory, searchText, plugins, myPluginIds]);

  const handleAddPlugin = async (plugin) => {
    if (!selectedSite) return;
    try {
      await callPiggybackAPI(
        "pluginSettings",
        {},
        {
          pluginId: plugin.id,
          siteId: selectedSite.id,
          tenantId: selectedSite.tenantId,
          settings: {},
        },
        "POST"
      );
      setMyPluginIds((prev) => (prev ? [...prev, plugin.id] : [plugin.id]));
      toast.success(`${plugin.title} added to My Plugins`);
    } catch (err) {
      console.error("Failed to add plugin:", err);
      toast.error("Failed to add plugin. Please try again.");
    }
  };

  const handleAddLabel = async () => {
    if (!newLabelName.trim() || !selectedSite) return;
    setAddingLabel(true);
    try {
      await callPiggybackAPI(
        "labels",
        {},
        {
          name: newLabelName.trim(),
          url: newLabelImageUrl.trim(),
          tenantId: selectedSite.tenantId,
          siteId: selectedSite.id,
        },
        "POST"
      );
      toast.success(`Label "${newLabelName.trim()}" added`);
      setNewLabelName("");
      setNewLabelImageUrl("");
      const refreshed = await callPiggybackAPI(
        "labels",
        { tenantId: selectedSite.tenantId, siteId: selectedSite.id },
        {},
        "GET"
      );
      setLabels(Array.isArray(refreshed) ? refreshed : []);
    } catch (err) {
      console.error("Failed to add label:", err);
      toast.error("Failed to add label. Please try again.");
    } finally {
      setAddingLabel(false);
    }
  };

  const handleOpenSmartSettings = async (plugin) => {
    setSmartPlugin(plugin);
    setSmartSettingsOpen(true);
    setSmartLoading(true);
    try {
      const [dataInfoList, settingsList, labelsList] = await Promise.all([
        callPiggybackAPI(
          "data-info",
          { tenantId: selectedSite?.tenantId, siteId: selectedSite?.id },
          {},
          "GET"
        ),
        callPiggybackAPI(
          "pluginSettings",
          { siteId: selectedSite?.id, tenantId: selectedSite?.tenantId },
          {},
          "GET"
        ),
        callPiggybackAPI(
          "labels",
          { tenantId: selectedSite?.tenantId, siteId: selectedSite?.id },
          {},
          "GET"
        ),
      ]);
      const fields = dataInfoList?.[0]?.fields ? Object.keys(dataInfoList[0].fields) : [];
      setAvailableFields(fields);
      const parsedLabels = Array.isArray(labelsList) ? labelsList : [];
      console.log("[SmartSettings] labels:", parsedLabels);
      setLabels(parsedLabels);
      console.log("[SmartSettings] settingsList:", settingsList);
      const existingEntry = Array.isArray(settingsList)
        ? settingsList.find((s) => s.pluginId === plugin.id) ?? null
        : null;
      console.log("[SmartSettings] existingEntry:", existingEntry);
      const existingSettings = existingEntry?.settings || {};
      console.log("[SmartSettings] existingSettings:", existingSettings);
      console.log("[SmartSettings] type:", existingSettings.type, "| column:", existingSettings.columnName, "| spacing:", existingSettings.spacing);
      setSmartColumn(existingSettings.columnName || "");
      setSmartSpacing(existingSettings.spacing !== undefined ? String(existingSettings.spacing) : "");
    } catch (err) {
      console.error("Failed to load smart settings:", err);
      setAvailableFields([]);
      setLabels([]);
      setSmartColumn("");
      setSmartSpacing("");
    } finally {
      setSmartLoading(false);
    }
  };

  const handleSaveSmartSettings = async () => {
    if (!smartPlugin || !selectedSite) return;
    setSmartSaving(true);
    try {
      await callPiggybackAPI(
        "pluginSettings",
        {},
        {
          pluginId: smartPlugin.id,
          siteId: selectedSite.id,
          tenantId: selectedSite.tenantId,
          settings: {
            ...(smartColumn && { columnName: smartColumn }),
            ...(smartSpacing !== "" && { spacing: Number(smartSpacing) }),
          },
        },
        "POST"
      );
      toast.success("Settings saved");
      setSmartSettingsOpen(false);
    } catch (err) {
      console.error("Failed to save smart settings:", err);
      toast.error("Failed to save settings. Please try again.");
    } finally {
      setSmartSaving(false);
    }
  };

  const handleOpenClick = async (plugin) => {
    if (NAVIGATABLE_OPTIONS.includes(plugin.optionName)) {
      dispatch(setTruInkSelectedSiteOption(plugin.optionName));
      return;
    }

    if (SMART_SETTINGS_PLUGINS.includes(plugin.title)) {
      await handleOpenSmartSettings(plugin);
      return;
    }

    setSelectedPlugin(plugin);
    console.log("[PluginsPanel] plugin data:", plugin);

    const rawSchedule = plugin.schedule ?? plugin.schedules ?? plugin.customProperties?.schedule;
    const siteSchedules = Array.isArray(rawSchedule)
      ? rawSchedule.filter((s) => s.siteId === selectedSite?.id)
      : [];
    const existing = siteSchedules.map((s) => {
      const ui = parseCronToUI(s.cronExpression);
      return {
        cronExpression: s.cronExpression || "",
        collectionId: s.collectionId || "",
        templateId: s.templateId || "",
        ...ui,
      };
    });
    setSchedules(existing);

    setConfigOpen(true);
    setConfigLoading(true);
    try {
      const [cols, tmps] = await Promise.all([
        callPiggybackAPI("collections", { siteId: selectedSite?.id, tenantId: selectedSite?.tenantId }, {}, "GET"),
        callPiggybackAPI("templates", { siteId: selectedSite?.id, tenantId: selectedSite?.tenantId }, {}, "GET"),
      ]);
      setCollections(Array.isArray(cols) ? cols : []);
      setTemplates(Array.isArray(tmps) ? tmps : []);
    } catch (err) {
      console.error("Failed to fetch collections/templates:", err);
    } finally {
      setConfigLoading(false);
    }
  };

  const addScheduleRow = () => setSchedules((prev) => [...prev, { ...EMPTY_SCHEDULE }]);

  const removeScheduleRow = async (index) => {
    const updated = schedules.filter((_, i) => i !== index);
    setSchedules(updated);
    if (updated.length === 0 && selectedPlugin) {
      try {
        await callPiggybackAPI("plugins", { pluginId: selectedPlugin.id }, { schedule: [] }, "PATCH");
        setPlugins((prev) =>
          prev.map((p) => (p.id === selectedPlugin.id ? { ...p, schedule: [] } : p))
        );
        toast.success("Schedule cleared");
      } catch (err) {
        console.error("Failed to clear schedule:", err);
        toast.error("Failed to clear schedule. Please try again.");
      }
    }
  };

  const updateScheduleRow = (index, field, value) =>
    setSchedules((prev) => prev.map((s, i) => (i === index ? { ...s, [field]: value } : s)));

  const updateCronField = (index, field, value) =>
    setSchedules((prev) =>
      prev.map((row, i) => {
        if (i !== index) return row;
        const updated = { ...row, [field]: value };
        if (updated._freq !== "custom") {
          updated.cronExpression = buildCron(updated._freq, updated._hour, updated._minute, updated._dow, updated._dom);
        }
        return updated;
      })
    );

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      const body = {
        schedule: schedules.map(({ cronExpression, collectionId, templateId }) => ({
          cronExpression,
          collectionId,
          templateId,
          tenantId: selectedSite?.tenantId,
          siteId: selectedSite?.id,
        })),
      };
      await callPiggybackAPI("plugins", { pluginId: selectedPlugin.id }, body, "PATCH");
      toast.success("Plugin configuration saved");
      setPlugins((prev) =>
        prev.map((p) => (p.id === selectedPlugin.id ? { ...p, schedule: body.schedule } : p))
      );
      setConfigOpen(false);
    } catch (err) {
      console.error("Failed to save config:", err);
      toast.error("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };
const paginatedPlugins = filteredPlugins.slice(
  (currentPage - 1) * PLUGINS_PER_PAGE,
  currentPage * PLUGINS_PER_PAGE
);

const totalPages = Math.ceil(filteredPlugins.length / PLUGINS_PER_PAGE);

useEffect(() => {
  setCurrentPage(1);
}, [searchText, selectedCategory]);

useEffect(() => {
  if (currentPage > totalPages && totalPages > 0) {
    setCurrentPage(1);
  }
}, [currentPage, totalPages]);
  return (
    <Box
      sx={{
        width: "100%",
        height: "calc(100vh - 260px)",
        minHeight: {xs:"unset",md:400,},
        display: "flex",
        flexDirection: "column",
      }}
    >

      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, flexWrap: "wrap" }}>
        {CATEGORIES.map((cat) => (
          <Button
            key={cat}
            onClick={() => setSelectedCategory(cat)}
            disableElevation
            sx={{
              textTransform: "none",

              minWidth: "unset",
              width: "fit-content",
              borderRadius: '10px',
              minHeight: 24,
              height: 24,
              fontSize: "0.75rem",
              px: 1,
              lineHeight: 1,
              ...(selectedCategory === cat
                ? {
                    color: "#fff",
                    background: "linear-gradient(90deg, rgb(58,130,150), rgb(63,88,111))",
                    "&:hover": {
                      background: "linear-gradient(90deg, rgb(58,130,150), rgb(63,88,111))",
                      opacity: 0.95,
                    },
                  }
                : {
                    color: "#555",
                    border: "1px solid #ccc",
                    backgroundColor: "transparent",
                    "&:hover": { backgroundColor: "#f5f5f5" },
                  }),
            }}
          >
            {cat}
          </Button>
        ))}
        <Box sx={{ ml: "auto" }}>
          <TextField
            size="small"
            placeholder="Search plugins..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ width: 240 }}
          />
        </Box>
      </Box>

    
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          "&::-webkit-scrollbar": { width: 4 },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": { background: "rgba(0,0,0,0.15)", borderRadius: 4 },
          "&::-webkit-scrollbar-thumb:hover": { background: "rgba(0,0,0,0.3)" },
        }}
      >
        {loading || myPluginIds === null ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress size={32} />
          </Box>
        ) : filteredPlugins.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 8, color: "text.secondary" }}>
            <Typography variant="body1">No plugins configured for this site.</Typography>
          </Box>
        ) : (
          <Box sx={{ display: "grid", gridTemplateColumns: {xs:"repeat(1, 1fr)",md:"repeat(3, 1fr)"}, gap: 2, pb: 3 }}>
            {paginatedPlugins.map((p) => (
              <Card
                key={p.id}
                elevation={1}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  transition: "box-shadow 0.15s",
                  position: "relative",
                  "&:hover": { boxShadow: 4 },
                }}
              >
                <CardContent sx={{ flex: 1, pb: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 1.5 }}>
                    <Avatar
                      src={p?.imageUrl}
                      variant="rounded"
                      sx={{ width: 48, height: 48, borderRadius: 1.5, flexShrink: 0 }}
                    />
                    <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 0.5, width:"100%" }}>
                      <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
                        <Typography variant="subtitle2" fontWeight={700} sx={{ lineHeight: 1.3 }}>
                          {p.title}
                        </Typography>
                        {p.customProperties?.isNew && (
                          <Chip
                            label="✨ New"
                            size="small"
                            sx={{
                              ml: "auto",
                              height: 18,
                              fontSize: "0.7rem",
                              fontWeight: 700,
                              backgroundColor: "transparent",
                              color: "primary.main",
                            }}
                          />
                        )}
                      </Box>
                      {p.customProperties?.developer && (
                        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2, textAlign: "left" }}>
                          {p.customProperties.developer}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  <Tooltip title={p.description} placement="top" arrow componentsProps={{ tooltip: { sx: { maxWidth: 260 } } }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        lineHeight: 1.5,
                        textAlign: "left",
                        mb: 1,
                        cursor: "default",
                      }}
                    >
                      {p.description}
                    </Typography>
                  </Tooltip>

                  <Chip label={p.category} size="small" sx={{ height: 20, fontSize: "0.65rem", display: "flex", width: "fit-content" }} />
                </CardContent>

                <Divider />

                <Box sx={{ px: 2, py: 1, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Chip label="Included" size="small" color="success" variant="outlined" sx={{ height: 20, fontSize: "0.65rem" }} />
                  <Button
                    size="small"
                    disableElevation
                    onClick={() => handleOpenClick(p)}
                    sx={{
                      textTransform: "none",
                      background: "linear-gradient(90deg, rgb(58,130,150), rgb(63,88,111))",
                      color: "#fff",
                      boxShadow: "none",
                      "&:hover": {
                        background: "linear-gradient(90deg, rgb(58,130,150), rgb(63,88,111))",
                        opacity: 0.95,
                        boxShadow: "none",
                      },
                    }}
                  >
                    Open
                  </Button>
                </Box>
              </Card>
            ))}
          </Box>

          
        )}
        {totalPages > 1 && !loading && filteredPlugins.length > 0 && (
  <Box sx={{ display: "flex", justifyContent: "center" }}>
    <Pagination
      count={totalPages}
      page={currentPage}
      onChange={(_, val) => setCurrentPage(val)}
      color="primary"
    />
  </Box>
)}
      </Box>

    
      <Dialog open={configOpen} onClose={() => setConfigOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>{selectedPlugin?.title} — Schedule Configuration</DialogTitle>
        <DialogContent sx={{ mt: 1 }}>
          {configLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {schedules.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 2 }}>
                  No schedules configured for this site.
                </Typography>
              )}
              {schedules.map((row, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.5,
                    p: 1.5,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1.5,
                  }}
                >
                 
                  <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", flexWrap: "wrap" }}>
                    <TextField
                      label="Frequency"
                      size="small"
                      select
                      value={row._freq}
                      onChange={(e) => updateCronField(index, "_freq", e.target.value)}
                      sx={{ width: 130 }}
                    >
                      <MenuItem value="daily">Daily</MenuItem>
                      <MenuItem value="weekly">Weekly</MenuItem>
                      <MenuItem value="monthly">Monthly</MenuItem>
                      <MenuItem value="custom">Custom</MenuItem>
                    </TextField>

                    {row._freq === "weekly" && (
                      <TextField
                        label="Day"
                        size="small"
                        select
                        value={row._dow}
                        onChange={(e) => updateCronField(index, "_dow", e.target.value)}
                        sx={{ width: 130 }}
                      >
                        {DOW_OPTIONS.map((d) => (
                          <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>
                        ))}
                      </TextField>
                    )}

                    {row._freq === "monthly" && (
                      <TextField
                        label="Day of Month"
                        size="small"
                        select
                        value={row._dom}
                        onChange={(e) => updateCronField(index, "_dom", e.target.value)}
                        sx={{ width: 130 }}
                      >
                        {Array.from({ length: 31 }, (_, i) => String(i + 1)).map((d) => (
                          <MenuItem key={d} value={d}>{d}</MenuItem>
                        ))}
                      </TextField>
                    )}

                    {row._freq !== "custom" && (
                      <>
                        <TextField
                          label="Hour"
                          size="small"
                          select
                          value={row._hour}
                          onChange={(e) => updateCronField(index, "_hour", e.target.value)}
                          sx={{ width: 90 }}
                        >
                          {HOURS.map((h) => (
                            <MenuItem key={h} value={h}>
                              {h.padStart(2, "0")}:00
                            </MenuItem>
                          ))}
                        </TextField>
                        <TextField
                          label="Minute"
                          size="small"
                          select
                          value={row._minute}
                          onChange={(e) => updateCronField(index, "_minute", e.target.value)}
                          sx={{ width: 80 }}
                        >
                          {MINUTES.map((m) => (
                            <MenuItem key={m} value={m}>:{m.padStart(2, "0")}</MenuItem>
                          ))}
                        </TextField>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ alignSelf: "center", fontFamily: "monospace", ml: 0.5 }}
                        >
                          {row.cronExpression}
                        </Typography>
                      </>
                    )}

                    {row._freq === "custom" && (
                      <TextField
                        label="Cron Expression"
                        placeholder="e.g. 0 9 * * 1"
                        size="small"
                        value={row.cronExpression}
                        onChange={(e) => updateScheduleRow(index, "cronExpression", e.target.value)}
                        sx={{ flex: 1, minWidth: 180 }}
                      />
                    )}
                  </Box>

                 
                  <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
                    <TextField
                      label="Collection"
                      size="small"
                      select
                      value={row.collectionId}
                      onChange={(e) => updateScheduleRow(index, "collectionId", e.target.value)}
                      sx={{ flex: 1 }}
                    >
                      <MenuItem value=""><em>None</em></MenuItem>
                      {collections.map((c) => (
                        <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      label="Template"
                      size="small"
                      select
                      value={row.templateId}
                      onChange={(e) => updateScheduleRow(index, "templateId", e.target.value)}
                      sx={{ flex: 1 }}
                    >
                      <MenuItem value=""><em>None</em></MenuItem>
                      {templates.map((t) => (
                        <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                      ))}
                    </TextField>
                    <IconButton
                      size="small"
                      onClick={() => setConfirmDeleteIndex(index)}
                      sx={{ mt: 0.5, color: "text.secondary" }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              ))}

              <Button
                startIcon={<AddIcon />}
                size="small"
                onClick={addScheduleRow}
                sx={{ alignSelf: "flex-start", textTransform: "none" }}
              >
                Add Schedule
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            variant="contained"
            onClick={() => setConfigOpen(false)}
            sx={{
              borderRadius: "4px",
              textTransform: "none",
              fontWeight: "bold",
              background: "linear-gradient(90deg, rgb(58,130,150), rgb(63,88,111))",
              color: "#fff",
              boxShadow: "none",
              "&:hover": {
                background: "linear-gradient(90deg, rgb(58,130,150), rgb(63,88,111))",
                opacity: 0.95,
                boxShadow: "none",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disableElevation
            onClick={handleSaveConfig}
            disabled={configLoading || saving}
            sx={{
              borderRadius: "4px",
              textTransform: "none",
              fontWeight: "bold",
              backgroundColor: "rgb(90, 28, 47)",
              "&:hover": { backgroundColor: "rgb(70, 20, 35)" },
              "&.Mui-disabled": { backgroundColor: "rgba(90, 28, 47, 0.4)", color: "#fff" },
            }}
          >
            {saving ? <CircularProgress size={18} sx={{ color: "inherit" }} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

   
      <Dialog open={confirmDeleteIndex !== null} onClose={() => setConfirmDeleteIndex(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Schedule</DialogTitle>
        <DialogContent>
          <Typography variant="body2">Are you sure you want to delete this schedule entry?</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            variant="contained"
            onClick={() => setConfirmDeleteIndex(null)}
            sx={{
              borderRadius: "4px",
              textTransform: "none",
              fontWeight: "bold",
              background: "linear-gradient(90deg, rgb(58,130,150), rgb(63,88,111))",
              color: "#fff",
              boxShadow: "none",
              "&:hover": {
                background: "linear-gradient(90deg, rgb(58,130,150), rgb(63,88,111))",
                opacity: 0.95,
                boxShadow: "none",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disableElevation
            onClick={() => {
              removeScheduleRow(confirmDeleteIndex);
              setConfirmDeleteIndex(null);
            }}
            sx={{
              borderRadius: "4px",
              textTransform: "none",
              fontWeight: "bold",
              backgroundColor: "rgb(90, 28, 47)",
              "&:hover": { backgroundColor: "rgb(70, 20, 35)" },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

   
      <Dialog open={smartSettingsOpen} onClose={() => setSmartSettingsOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>{smartPlugin?.title} — Settings</DialogTitle>
        <DialogContent sx={{ mt: 1 }}>
          {smartLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, mt: 1 }}>

              <Box sx={{ display: "flex", gap: 2 }}>
                <TextField
                  label="Source Column"
                  select
                  size="small"
                  value={smartColumn}
                  onChange={(e) => setSmartColumn(e.target.value)}
                  sx={{ flex: 1 }}
                >
                  <MenuItem value=""><em>Select column</em></MenuItem>
                  {availableFields.map((field) => (
                    <MenuItem key={field} value={field}>{field}</MenuItem>
                  ))}
                </TextField>

                <TextField
                  label="Spacing"
                  size="small"
                  type="number"
                  value={smartSpacing}
                  onChange={(e) => setSmartSpacing(e.target.value)}
                  sx={{ width: 140 }}
                  inputProps={{ min: 0 }}
                  placeholder="e.g. 10"
                />
              </Box>

              <Divider />

          
              <Box>
                <Typography variant="body2" fontWeight={600} sx={{ mb: 1.5 }}>Labels</Typography>

                {labels.length > 0 ? (
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "repeat(10, 1fr)",
                      gap: 1,
                      mb: 2,
                      maxHeight: 300,
                      overflowY: "auto",
                      pr: 0.5,
                      "&::-webkit-scrollbar": { width: 4 },
                      "&::-webkit-scrollbar-thumb": { background: "rgba(0,0,0,0.15)", borderRadius: 4 },
                    }}
                  >
                    {labels.map((lbl) => (
                      <Box
                        key={lbl.id}
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: 0.5,
                          p: 0.75,
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 1.5,
                          background: "#fff",
                        }}
                      >
                        <Avatar
                          src={lbl.url || undefined}
                          alt={lbl.name}
                          variant="rounded"
                          imgProps={{ style: { objectFit: "contain" } }}
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 1,
                            bgcolor: "#fff",
                            color: "text.secondary",
                            fontSize: "1.3rem",
                            fontWeight: 700,
                          }}
                        >
                          {lbl.name?.[0]?.toUpperCase() ?? "?"}
                        </Avatar>
                        <Typography
                          variant="caption"
                          sx={{
                            textAlign: "center",
                            lineHeight: 1.2,
                            wordBreak: "break-word",
                            fontWeight: 500,
                            color: "text.primary",
                          }}
                        >
                          {lbl.name}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    No labels added yet.
                  </Typography>
                )}

               
                <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
                  <TextField
                    label="Name"
                    size="small"
                    value={newLabelName}
                    onChange={(e) => setNewLabelName(e.target.value)}
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="Image URL"
                    size="small"
                    value={newLabelImageUrl}
                    onChange={(e) => setNewLabelImageUrl(e.target.value)}
                    sx={{ flex: 2 }}
                  />
                  <Button
                    variant="contained"
                    disableElevation
                    size="small"
                    disabled={!newLabelName.trim() || addingLabel}
                    onClick={handleAddLabel}
                    sx={{
                      textTransform: "none",
                      flexShrink: 0,
                      mt: 0.5,
                      background: "linear-gradient(90deg, rgb(58,130,150), rgb(63,88,111))",
                      color: "#fff",
                      boxShadow: "none",
                      "&:hover": { background: "linear-gradient(90deg, rgb(58,130,150), rgb(63,88,111))", opacity: 0.95 },
                      "&.Mui-disabled": { background: "rgba(0,0,0,0.08)", color: "rgba(0,0,0,0.38)" },
                    }}
                  >
                    {addingLabel ? <CircularProgress size={16} sx={{ color: "inherit" }} /> : "Add Label"}
                  </Button>
                </Box>
              </Box>

            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            variant="contained"
            onClick={() => setSmartSettingsOpen(false)}
            sx={{
              textTransform: "none",
              background: "linear-gradient(90deg, rgb(58,130,150), rgb(63,88,111))",
              color: "#fff",
              boxShadow: "none",
              "&:hover": { background: "linear-gradient(90deg, rgb(58,130,150), rgb(63,88,111))", opacity: 0.95, boxShadow: "none" },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disableElevation
            onClick={handleSaveSmartSettings}
            disabled={smartLoading || smartSaving}
            sx={{
              textTransform: "none",
              backgroundColor: "rgb(90, 28, 47)",
              "&:hover": { backgroundColor: "rgb(70, 20, 35)" },
              "&.Mui-disabled": { backgroundColor: "rgba(90, 28, 47, 0.4)", color: "#fff" },
            }}
          >
            {smartSaving ? <CircularProgress size={18} sx={{ color: "inherit" }} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default PluginsPanel;
